import {
  useCallback,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAsyncSubmit } from "@/hooks/useAsyncSubmit";
import { useMountedRef } from "@/hooks/useMountedRef";
import { useScopedUiScope } from "@/hooks/useScopedUiStorage";
import {
  extractPublicErrorMessage,
  isRequestCanceled,
} from "@/services/apiErrors";

interface InterfaceBuilderAsyncRunnerParams {
  errorFallbackKey: string;
  isAuthenticated: boolean;
  projectId?: string | null;
  userId?: string | null;
}

type ScopedInterfaceBuilderTask = (
  signal: AbortSignal,
  scopeKey: string,
) => Promise<boolean | void>;

export function useInterfaceBuilderAsyncRunner({
  errorFallbackKey,
  isAuthenticated,
  projectId,
  userId,
}: InterfaceBuilderAsyncRunnerParams) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const mountedRef = useMountedRef();
  const { submitting, submit } = useAsyncSubmit();
  const scope = useScopedUiScope({ projectId, userId });

  const clearError = useCallback(() => {
    if (!mountedRef.current) return;
    setError(null);
    setErrorDetails(null);
  }, [mountedRef]);

  const run = useCallback(
    async (task: ScopedInterfaceBuilderTask) => {
      if (!isAuthenticated || !scope) return false;

      let succeeded = false;
      clearError();

      await submit(async (signal) => {
        try {
          const result = await task(signal, scope.key);
          succeeded = result !== false;
        } catch (err) {
          if (signal.aborted || isRequestCanceled(err)) return;
          if (!mountedRef.current) return;

          // The visitor is told what failed in their own language. What the
          // platform answered names records, filters and fields -- a report for
          // whoever is building the page rather than a sentence to read -- so
          // it is kept apart, for the dialog to show on request.
          setError(t(errorFallbackKey));
          setErrorDetails(extractPublicErrorMessage(err, ""));
        }
      });

      return succeeded;
    },
    [
      clearError,
      errorFallbackKey,
      isAuthenticated,
      mountedRef,
      scope,
      submit,
      t,
    ],
  );

  return {
    clearError,
    error,
    errorDetails,
    run,
    submitting,
  };
}
